import React, { useState, useEffect, useCallback } from 'react';
import { Landmark, ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { deudaService } from '../services/deudaService';
import { DeudaConEstado } from '../lib/supabase';
import { OwnerContext } from '../types/actividad';
import { formatCurrency } from '../utils/formatters';
import { DeudaModal } from './DeudaModal';
import { DeudaDetalleModal } from './DeudaDetalleModal';

interface DeudasSectionProps {
  readonly owner: OwnerContext | null;
  readonly soloLectura?: boolean;
}

function esPropia(deuda: DeudaConEstado, owner: OwnerContext | null): boolean {
  if (!owner) return false;
  if (owner.tipo === 'lumisial') return deuda.lumisial_id === owner.id;
  if (owner.tipo === 'diocesis') return deuda.diocesis_id === owner.id;
  if (owner.tipo === 'provincia') return deuda.provincia_eclesiastica_id === owner.id;
  return false;
}

function entidadIdDeuda(deuda: DeudaConEstado): string {
  return deuda.lumisial_id ?? deuda.diocesis_id ?? deuda.provincia_eclesiastica_id ?? deuda.id;
}

// ---------------------------------------------------------------------------
// Card de deuda
// ---------------------------------------------------------------------------

interface DeudaCardProps {
  readonly deuda: DeudaConEstado;
  readonly onClick: () => void;
}

const DeudaCard: React.FC<DeudaCardProps> = ({ deuda, onClick }) => {
  const progreso = deuda.numero_cuotas > 0 ? Math.min(100, (deuda.cuotasPagadas / deuda.numero_cuotas) * 100) : 0;

  return (
    <div className="relative border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors bg-white">
      <button type="button" onClick={onClick} className="absolute inset-0 rounded-xl cursor-pointer" aria-label={`Ver detalle de deuda con ${deuda.acreedor}`} />
      <div className="relative flex items-center justify-between gap-4 pointer-events-none">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-800 truncate">{deuda.acreedor}</p>
          {deuda.descripcion && <p className="text-sm text-slate-500 truncate">{deuda.descripcion}</p>}
          <div className="flex items-center gap-2 mt-2">
            <div className="w-32 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full" style={{ width: `${progreso}%` }} />
            </div>
            <span className="text-xs text-slate-500">{deuda.cuotasPagadas} de {deuda.numero_cuotas} cuotas</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-semibold text-slate-800">{formatCurrency(deuda.saldo)}</p>
          <p className="text-xs text-slate-400">saldo</p>
          {deuda.cuotasAtrasadas > 0 && (
            <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
              {deuda.cuotasAtrasadas} {deuda.cuotasAtrasadas === 1 ? 'cuota atrasada' : 'cuotas atrasadas'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Grupo de deudas de una entidad subordinada
// ---------------------------------------------------------------------------

interface GrupoSubordinadoProps {
  readonly entidadNombre: string;
  readonly deudas: DeudaConEstado[];
  readonly onSeleccionar: (deuda: DeudaConEstado) => void;
}

const GrupoSubordinado: React.FC<GrupoSubordinadoProps> = ({ entidadNombre, deudas, onSeleccionar }) => (
  <div className="space-y-2">
    <h4 className="text-sm font-semibold text-slate-600">{entidadNombre}</h4>
    <div className="space-y-2">
      {deudas.map((d) => <DeudaCard key={d.id} deuda={d} onClick={() => onSeleccionar(d)} />)}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Estado vacío
// ---------------------------------------------------------------------------

interface EstadoVacioProps {
  readonly puedeCrear: boolean;
  readonly onCrear: () => void;
}

const EstadoVacio: React.FC<EstadoVacioProps> = ({ puedeCrear, onCrear }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
    <Landmark className="w-10 h-10 text-slate-300 mx-auto mb-3" />
    <p className="text-slate-500 font-medium">Sin deudas registradas</p>
    {puedeCrear && (
      <button
        type="button"
        onClick={onCrear}
        className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
      >
        <Plus className="w-4 h-4" /> Registrar deuda
      </button>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export const DeudasSection: React.FC<DeudasSectionProps> = ({ owner, soloLectura = false }) => {
  const [loading, setLoading] = useState(true);
  const [deudas, setDeudas] = useState<DeudaConEstado[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [deudaSeleccionada, setDeudaSeleccionada] = useState<DeudaConEstado | null>(null);
  const [historialAbierto, setHistorialAbierto] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await deudaService.getVisibles();
      setDeudas(data);
    } catch {
      // error silencioso — la UI muestra estado vacío
      setDeudas([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse space-y-3">
        <div className="h-4 bg-slate-200 rounded w-32" />
        <div className="h-16 bg-slate-100 rounded-xl" />
        <div className="h-16 bg-slate-100 rounded-xl" />
      </div>
    );
  }

  // Un lumisial es el nivel más bajo de la jerarquía — no tiene subordinados. `getVisibles()`
  // trae todo lo que la RLS deja ver al usuario (para JAD/JAN/admin_global eso puede incluir
  // deudas de otras entidades ajenas a este lumisial); sin este guard, esas deudas ajenas se
  // mostraban como "subordinadas" del lumisial. Solo diócesis/provincia tienen subordinados reales.
  const esEntidadHoja = owner?.tipo === 'lumisial';

  const activas = deudas.filter(d => d.estado === 'activa');
  const historial = deudas.filter(d => d.estado !== 'activa');

  const propiasActivas = activas.filter(d => esPropia(d, owner));
  const subordinadasActivas = esEntidadHoja ? [] : activas.filter(d => !esPropia(d, owner));
  const gruposSubordinadosActivas = agruparPorEntidad(subordinadasActivas);

  const propiasHistorial = historial.filter(d => esPropia(d, owner));
  const subordinadasHistorial = esEntidadHoja ? [] : historial.filter(d => !esPropia(d, owner));
  const gruposSubordinadosHistorial = agruparPorEntidad(subordinadasHistorial);

  const puedeCrear = owner !== null && !soloLectura;
  const seleccionEsPropia = deudaSeleccionada ? esPropia(deudaSeleccionada, owner) : false;
  const detalleSoloLectura = soloLectura || !seleccionEsPropia;

  const hayContenido =
    propiasActivas.length > 0 || gruposSubordinadosActivas.length > 0 ||
    propiasHistorial.length > 0 || gruposSubordinadosHistorial.length > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-slate-800">Deudas</h3>
        {puedeCrear && (
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" /> Registrar deuda
          </button>
        )}
      </div>

      {!hayContenido && <EstadoVacio puedeCrear={puedeCrear} onCrear={() => setShowModal(true)} />}

      {propiasActivas.length > 0 && (
        <div className="space-y-2">
          {propiasActivas.map((d) => <DeudaCard key={d.id} deuda={d} onClick={() => setDeudaSeleccionada(d)} />)}
        </div>
      )}

      {gruposSubordinadosActivas.map(([entidadId, { nombre, items }]) => (
        <GrupoSubordinado key={entidadId} entidadNombre={nombre} deudas={items} onSeleccionar={setDeudaSeleccionada} />
      ))}

      {historial.length > 0 && (
        <div className="border-t border-slate-200 pt-3">
          <button
            type="button"
            onClick={() => setHistorialAbierto(prev => !prev)}
            className="flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-800 transition-colors"
          >
            {historialAbierto ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            Historial ({historial.length})
          </button>
          {historialAbierto && (
            <div className="mt-3 space-y-3">
              {propiasHistorial.map((d) => <DeudaCard key={d.id} deuda={d} onClick={() => setDeudaSeleccionada(d)} />)}
              {gruposSubordinadosHistorial.map(([entidadId, { nombre, items }]) => (
                <GrupoSubordinado key={entidadId} entidadNombre={nombre} deudas={items} onSeleccionar={setDeudaSeleccionada} />
              ))}
            </div>
          )}
        </div>
      )}

      {showModal && owner && (
        <DeudaModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); cargar(); }}
          owner={owner}
        />
      )}

      {deudaSeleccionada && (
        <DeudaDetalleModal
          isOpen
          onClose={() => setDeudaSeleccionada(null)}
          deuda={deudaSeleccionada}
          soloLectura={detalleSoloLectura}
          onChanged={() => cargar()}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Helper de agrupamiento (extraído para reducir complejidad cognitiva)
// ---------------------------------------------------------------------------

function agruparPorEntidad(items: DeudaConEstado[]): [string, { nombre: string; items: DeudaConEstado[] }][] {
  const grupos = new Map<string, { nombre: string; items: DeudaConEstado[] }>();
  for (const d of items) {
    const id = entidadIdDeuda(d);
    const grupo = grupos.get(id);
    if (grupo) grupo.items.push(d);
    else grupos.set(id, { nombre: d.entidadNombre, items: [d] });
  }
  return Array.from(grupos.entries());
}
